import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useClienteAuth } from "@/contexts/ClienteAuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Building2, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { logoColor } from "@/lib/logos";

const F = ({ label, req, children }) => (
  <div>
    <Label className="text-sm">{label}{req && <span className="text-red-500"> *</span>}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function ClienteAcceso() {
  const navigate = useNavigate();
  const { api, loginWithToken } = useClienteAuth();
  const [mode, setMode] = useState("login"); // login | registro | verificar | forgot | reset
  const [busy, setBusy] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [f, setF] = useState({ nombre: "", email: "", telefono: "", password: "", password2: "",
    razon_social: "", rut: "", direccion: "", comuna: "", region: "", codigo: "" });
  const u = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  const login = () => run(async () => {
    setLoginError("");
    if (!f.email || !f.password) { setLoginError("Ingresa tu correo y contraseña"); return; }
    try {
      const r = await api.post("/api/portal/login", { email: f.email, password: f.password });
      loginWithToken(r.data.token);
      navigate("/cliente/dashboard");
    } catch (e) {
      const d = e.response?.data?.detail || "No pudimos iniciar sesión. Revisa tu conexión.";
      if (e.response?.status === 403 && d.includes("verificar")) { setMode("verificar"); toast.info(d); }
      else { setLoginError(d); toast.error(d); }
    }
  });

  const registrar = () => run(async () => {
    if (f.password.length < 8) return toast.error("La contraseña debe tener al menos 8 caracteres");
    if (f.password !== f.password2) return toast.error("Las contraseñas no coinciden");
    if (!f.nombre || !f.email) return toast.error("Completa nombre y correo");
    if (!f.rut.trim()) return toast.error("El RUT de la clínica es obligatorio: es el que une a todos los usuarios de tu organización");
    try {
      const r = await api.post("/api/portal/register", {
        nombre: f.nombre, email: f.email, telefono: f.telefono, password: f.password,
        razon_social: f.razon_social, rut: f.rut, direccion: f.direccion,
        comuna: f.comuna, region: f.region,
      });
      if (r.data.verificacion) { setMode("verificar"); toast.success("Revisa tu correo: te enviamos un código"); }
      else { toast.success("Cuenta creada"); setMode("login"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Error al registrarse"); }
  });

  const verificar = () => run(async () => {
    try {
      const r = await api.post("/api/portal/verify", { email: f.email, codigo: f.codigo });
      loginWithToken(r.data.token);
      toast.success("¡Cuenta verificada!");
      navigate("/cliente/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Código incorrecto"); }
  });

  const forgot = () => run(async () => {
    await api.post("/api/portal/forgot", { email: f.email }).catch(() => {});
    toast.success("Si la cuenta existe, enviamos un código a tu correo");
    setMode("reset");
  });

  const reset = () => run(async () => {
    if (f.password.length < 8) return toast.error("Mínimo 8 caracteres");
    try {
      await api.post("/api/portal/reset", { email: f.email, codigo: f.codigo, password: f.password });
      toast.success("Contraseña actualizada");
      setMode("login");
    } catch (e) { toast.error(e.response?.data?.detail || "Código inválido"); }
  });

  const inp = (k, props = {}) => (
    <Input value={f[k]} onChange={(e) => u(k, e.target.value)} className="h-11 bg-white" {...props} />
  );

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src={logoColor} alt="Odontofix" className="h-24 mx-auto" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-[#384091]">Portal de Clientes</h1>
          <p className="text-sm text-gray-500 mt-1">Tus equipos, documentos y mantenciones en un solo lugar</p>
        </div>

        <Card className="border border-gray-200 shadow-lg">
          <CardContent className="p-6 space-y-4">
            {mode === "login" && (<>
              {loginError && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {loginError}
                </div>
              )}
              <F label="Correo" req>{inp("email", { type: "email", className: "h-11 bg-white no-uppercase", placeholder: "tucorreo@clinica.cl" })}</F>
              <F label="Contraseña" req>{inp("password", { type: "password", className: "h-11 bg-white no-uppercase" })}</F>
              <Button className="w-full h-12 bg-[#0FB4B7] hover:bg-[#0D999C] text-white" onClick={login} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ingresar"}
              </Button>
              <div className="flex justify-between text-sm">
                <button className="text-[#0FB4B7] hover:underline" onClick={() => setMode("forgot")}>Olvidé mi contraseña</button>
                <button className="text-[#384091] font-semibold hover:underline" onClick={() => setMode("registro")}>Crear cuenta</button>
              </div>
            </>)}

            {mode === "registro" && (<>
              <p className="text-sm font-semibold text-[#384091] flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Datos de tu clínica
              </p>
              <F label="Tu nombre" req>{inp("nombre", { placeholder: "Nombre y apellido" })}</F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Correo" req>{inp("email", { type: "email", className: "h-11 bg-white no-uppercase" })}</F>
                <F label="Teléfono">{inp("telefono", { type: "tel", className: "h-11 bg-white no-uppercase", placeholder: "+56 9…" })}</F>
              </div>
              <F label="Razón social / nombre clínica">{inp("razon_social")}</F>
              <p className="text-[11px] text-gray-400 -mt-1">La clínica se crea una sola vez con su RUT. Si tus colegas se registran con el mismo RUT, entran a la misma clínica (mismos equipos y documentos, cada uno con su sesión).</p>
              <div className="grid grid-cols-2 gap-3">
                <F label="RUT clínica" req>{inp("rut", { className: "h-11 bg-white no-uppercase", placeholder: "76.299.480-K" })}</F>
                <F label="Comuna">{inp("comuna")}</F>
              </div>
              <F label="Dirección">{inp("direccion")}</F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Contraseña" req>{inp("password", { type: "password", className: "h-11 bg-white no-uppercase", placeholder: "Mín. 8 caracteres" })}</F>
                <F label="Repite contraseña" req>{inp("password2", { type: "password", className: "h-11 bg-white no-uppercase" })}</F>
              </div>
              <Button className="w-full h-12 bg-[#384091] hover:bg-[#2D3375] text-white" onClick={registrar} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear cuenta"}
              </Button>
              <button className="text-sm text-gray-500 hover:underline w-full" onClick={() => setMode("login")}>Ya tengo cuenta</button>
            </>)}

            {mode === "verificar" && (<>
              <p className="text-sm text-gray-600 flex items-start gap-2">
                <ShieldCheck className="h-5 w-5 text-[#0FB4B7] flex-shrink-0" />
                Te enviamos un código de 6 dígitos a <b>{f.email || "tu correo"}</b>.
              </p>
              {!f.email && <F label="Correo" req>{inp("email", { type: "email", className: "h-11 bg-white no-uppercase" })}</F>}
              <F label="Código de verificación" req>
                {inp("codigo", { inputMode: "numeric", maxLength: 6, className: "h-12 bg-white no-uppercase text-center text-2xl tracking-[0.5em]" })}
              </F>
              <Button className="w-full h-12 bg-[#0FB4B7] hover:bg-[#0D999C] text-white" onClick={verificar} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verificar y entrar"}
              </Button>
              <button className="text-sm text-[#0FB4B7] hover:underline w-full" disabled={busy}
                onClick={() => run(async () => { await api.post("/api/portal/resend-code", { email: f.email }); toast.success("Código reenviado"); })}>
                Reenviar código
              </button>
            </>)}

            {mode === "forgot" && (<>
              <F label="Correo de tu cuenta" req>{inp("email", { type: "email", className: "h-11 bg-white no-uppercase" })}</F>
              <Button className="w-full h-12 bg-[#0FB4B7] hover:bg-[#0D999C] text-white" onClick={forgot} disabled={busy}>
                Enviar código de recuperación
              </Button>
              <button className="text-sm text-gray-500 hover:underline w-full" onClick={() => setMode("login")}>Volver</button>
            </>)}

            {mode === "reset" && (<>
              <F label="Código recibido" req>
                {inp("codigo", { inputMode: "numeric", maxLength: 6, className: "h-12 bg-white no-uppercase text-center text-2xl tracking-[0.5em]" })}
              </F>
              <F label="Nueva contraseña" req>{inp("password", { type: "password", className: "h-11 bg-white no-uppercase", placeholder: "Mín. 8 caracteres" })}</F>
              <Button className="w-full h-12 bg-[#384091] hover:bg-[#2D3375] text-white" onClick={reset} disabled={busy}>
                Cambiar contraseña
              </Button>
              <button className="text-sm text-gray-500 hover:underline w-full" onClick={() => setMode("login")}>Volver</button>
            </>)}
          </CardContent>
        </Card>

        <div className="mt-5 flex items-center justify-center gap-5 text-sm">
          <Link to="/" className="flex items-center gap-1 text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-3.5 w-3.5" /> Volver a odontofix.cl
          </Link>
          <button className="text-gray-400 hover:text-gray-600 underline"
            onClick={async () => {
              const detalle = window.prompt("Cuéntanos el problema (ej: alguien usó el RUT de mi clínica para crear la cuenta):");
              if (!detalle) return;
              try {
                await api.post("/api/portal/reportar-problema", { nombre: f.nombre, email: f.email, rut: f.rut, detalle });
                toast.success("Reporte enviado, te contactaremos a la brevedad");
              } catch (e) { toast.error(e.response?.data?.detail || "No se pudo enviar"); }
            }}>
            Reportar un problema
          </button>
        </div>
      </div>
    </div>
  );
}
